import React, { useState, useEffect } from "react";
import { Plus, X, Save, Calendar, Target, AlertCircle, CheckCircle2, Clock, Edit2, Trash2 } from "lucide-react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchAPDPsByTeacher,
  createAPDP,
  createMultipleAPDPs,
  updateAPDP,
  deleteAPDP,
  clearError,
  resetAPDPs,
} from "../../../redux/annualPersonalDevelopmentPlanSlice";

const MyAPDP = ({ teacherEmail, isModalOpen = true }) => {
  const dispatch = useDispatch();
  const { plans, plansByYear, loading, error, count } = useSelector(
    (state) => state.annualPersonalDevelopmentPlan
  );

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isYearPlansModalOpen, setIsYearPlansModalOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    performanceYear: new Date().getFullYear().toString(),
    planName: "",
    activities: "",
    targetDate: "",
    expectedOutcome: "",
  });

  // Fetch plans when component mounts or modal opens
  useEffect(() => {
    if (teacherEmail && isModalOpen) {
      dispatch(resetAPDPs());
      dispatch(fetchAPDPsByTeacher(teacherEmail));
    }
  }, [dispatch, teacherEmail, isModalOpen]);

  // Handle errors
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleAddPlan = () => {
    setFormData({
      performanceYear: new Date().getFullYear().toString(),
      planName: "",
      activities: "",
      targetDate: "",
      expectedOutcome: "",
    });
    setEditingPlan(null);
    setIsAddModalOpen(true);
  };

  const handleEditPlan = (plan) => {
    // Only allow editing if status is pending or rejected
    if (plan.status === "accepted") {
      toast.error("Cannot edit an accepted plan. It is locked for the performance year.");
      return;
    }
    setEditingPlan(plan);
    setFormData({
      performanceYear: plan.performanceYear.toString(),
      planName: plan.planName,
      activities: plan.activities,
      targetDate: plan.targetDate ? new Date(plan.targetDate).toISOString().split("T")[0] : "",
      expectedOutcome: plan.expectedOutcome,
    });
    setIsEditModalOpen(true);
  };

  const handleSubmitPlan = async (e) => {
    e.preventDefault();
    
    if (!formData.planName.trim() || !formData.activities.trim() || !formData.targetDate || !formData.expectedOutcome.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const planPayload = {
        teacherEmail,
        performanceYear: parseInt(formData.performanceYear),
        planName: formData.planName.trim(),
        activities: formData.activities.trim(),
        targetDate: formData.targetDate,
        expectedOutcome: formData.expectedOutcome.trim(),
      };

      if (editingPlan) {
        await dispatch(updateAPDP({ planId: editingPlan.id, updateData: planPayload })).unwrap();
        toast.success("Plan updated successfully");
        setIsEditModalOpen(false);
      } else {
        await dispatch(createAPDP(planPayload)).unwrap();
        toast.success("Plan created and sent for admin review");
        setIsAddModalOpen(false);
      }
      
      setFormData({
        performanceYear: new Date().getFullYear().toString(),
        planName: "",
        activities: "",
        targetDate: "",
        expectedOutcome: "",
      });
      setEditingPlan(null);
      dispatch(fetchAPDPsByTeacher(teacherEmail));
    } catch (err) {
      toast.error(err || "Failed to save plan");
    }
  };

  const handleDeletePlan = async (planId) => {
    const plan = plans.find((p) => p.id === planId);
    if (plan.status === "accepted") {
      toast.error("Cannot delete an accepted plan");
      return;
    }
    if (window.confirm("Are you sure you want to delete this plan?")) {
      try {
        await dispatch(deleteAPDP(planId)).unwrap();
        toast.success("Plan deleted successfully");
        dispatch(fetchAPDPsByTeacher(teacherEmail));
      } catch (err) {
        toast.error(err || "Failed to delete plan");
      }
    }
  };

  const handleViewYearPlans = (year) => {
    setSelectedYear(year);
    setIsYearPlansModalOpen(true);
  };

  const years = Object.keys(plansByYear || {}).sort((a, b) => b.localeCompare(a));

  const getStatusIcon = (status) => {
    switch (status) {
      case "accepted":
        return <CheckCircle2 size={16} className="text-green-500" />;
      case "rejected":
        return <AlertCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-yellow-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "accepted":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      default:
        return "bg-yellow-100 text-yellow-800";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#241763] to-[#3d2a7a] rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target size={28} className="text-yellow-300" />
            <div>
              <h2 className="text-2xl font-bold">Annual Personal Development Plan (APDP)</h2>
              <p className="text-gray-200 text-sm mt-1">
                Create and manage your personal development plans for each performance year
              </p>
            </div>
          </div>
          <button
            onClick={handleAddPlan}
            className="bg-white text-[#241763] px-6 py-2 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2"
          >
            <Plus size={20} />
            Create Plan
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-2 border-yellow-300 rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-yellow-800 mb-1">Pending Review</p>
              <p className="text-3xl font-bold text-yellow-700">
                {count?.pending || 0}
              </p>
              <p className="text-xs text-yellow-600 mt-1">Awaiting admin approval</p>
            </div>
            <div className="bg-yellow-300 rounded-full p-4 shadow-inner">
              <Clock className="text-yellow-800" size={28} />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-green-50 to-green-100 border-2 border-green-300 rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-800 mb-1">Accepted</p>
              <p className="text-3xl font-bold text-green-700">
                {count?.accepted || 0}
              </p>
              <p className="text-xs text-green-600 mt-1">Locked for the year</p>
            </div>
            <div className="bg-green-300 rounded-full p-4 shadow-inner">
              <CheckCircle2 className="text-green-800" size={28} />
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-red-50 to-red-100 border-2 border-red-300 rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-red-800 mb-1">Rejected</p>
              <p className="text-3xl font-bold text-red-700">
                {count?.rejected || 0}
              </p>
              <p className="text-xs text-red-600 mt-1">Can be edited</p>
            </div>
            <div className="bg-red-300 rounded-full p-4 shadow-inner">
              <AlertCircle className="text-red-800" size={28} />
            </div>
          </div>
        </div>
      </div>

      {/* Plans by Year List */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <Calendar className="text-[#241763]" size={24} />
          <h3 className="text-xl font-bold text-gray-800">Plans by Year</h3>
        </div>
        {loading ? (
          <div className="text-center py-8 text-gray-500">
            <p>Loading plans...</p>
          </div>
        ) : years.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {years.map((year) => {
              const yearPlans = plansByYear[year] || [];
              const pendingCount = yearPlans.filter((p) => p.status === "pending").length;
              const acceptedCount = yearPlans.filter((p) => p.status === "accepted").length;
              const rejectedCount = yearPlans.filter((p) => p.status === "rejected").length;
              
              return (
                <div
                  key={year}
                  className="bg-white border-2 border-gray-200 rounded-xl p-5 hover:border-[#241763] hover:shadow-lg cursor-pointer transition-all duration-200 group"
                  onClick={() => handleViewYearPlans(year)}
                >
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center gap-3">
                      <div className="bg-[#241763] text-white rounded-lg p-2 group-hover:bg-[#3d2a7a] transition-colors">
                        <Calendar size={20} />
                      </div>
                      <div>
                        <h4 className="font-bold text-xl text-[#241763]">Year {year}</h4>
                        <p className="text-sm text-gray-500 mt-1">
                          {yearPlans.length} plan{yearPlans.length !== 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>
                    <span className="text-[#241763] group-hover:text-[#3d2a7a] font-semibold">View →</span>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-4">
                    {pendingCount > 0 && (
                      <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 px-3 py-1.5 rounded-full text-xs font-semibold">
                        <Clock size={14} />
                        {pendingCount} Pending
                      </span>
                    )}
                    {acceptedCount > 0 && (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-800 px-3 py-1.5 rounded-full text-xs font-semibold">
                        <CheckCircle2 size={14} />
                        {acceptedCount} Accepted
                      </span>
                    )}
                    {rejectedCount > 0 && (
                      <span className="inline-flex items-center gap-1 bg-red-100 text-red-800 px-3 py-1.5 rounded-full text-xs font-semibold">
                        <AlertCircle size={14} />
                        {rejectedCount} Rejected
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-xl p-12 text-center">
            <Target className="mx-auto text-gray-400 mb-4" size={48} />
            <p className="text-gray-600 font-medium text-lg mb-2">No plans created yet</p>
            <p className="text-gray-500 text-sm mb-4">Click "Create Plan" to start your personal development journey.</p>
            <button
              onClick={handleAddPlan}
              className="bg-[#241763] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#1a1247] transition-colors inline-flex items-center gap-2"
            >
              <Plus size={20} />
              Create Your First Plan
            </button>
          </div>
        )}
      </div>

      {/* Add/Edit Plan Modal */}
      {(isAddModalOpen || isEditModalOpen) && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
            <div className="sticky top-0 bg-gradient-to-r from-[#241763] to-[#3d2a7a] text-white p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold">
                {editingPlan ? "Edit Plan" : "Create New Plan"}
              </h2>
              <button
                onClick={() => {
                  setIsAddModalOpen(false);
                  setIsEditModalOpen(false);
                  setEditingPlan(null);
                  setFormData({
                    performanceYear: new Date().getFullYear().toString(),
                    planName: "",
                    activities: "",
                    targetDate: "",
                    expectedOutcome: "",
                  });
                }}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmitPlan} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Performance Year <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.performanceYear}
                  onChange={(e) => setFormData({ ...formData, performanceYear: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
                  placeholder="e.g., 2025"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.planName}
                  onChange={(e) => setFormData({ ...formData, planName: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
                  placeholder="Enter plan name..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Activities <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.activities}
                  onChange={(e) => setFormData({ ...formData, activities: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
                  rows="4"
                  placeholder="Describe the activities for this plan..."
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Target Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.targetDate}
                  onChange={(e) => setFormData({ ...formData, targetDate: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Outcome <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={formData.expectedOutcome}
                  onChange={(e) => setFormData({ ...formData, expectedOutcome: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-[#241763]"
                  rows="4"
                  placeholder="Describe the expected outcome..."
                  required
                />
              </div>
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setIsEditModalOpen(false);
                    setEditingPlan(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#241763] text-white rounded-lg hover:bg-[#1a1247] flex items-center gap-2"
                >
                  <Save size={16} />
                  {editingPlan ? "Update Plan" : "Create Plan"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Year Plans Modal */}
      {isYearPlansModalOpen && selectedYear && (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col relative">
            <div className="bg-gradient-to-r from-[#241763] to-[#3d2a7a] text-white p-6 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-white/20 rounded-lg p-2">
                  <Calendar size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold">Plans for Year {selectedYear}</h2>
                  <p className="text-gray-200 text-sm mt-1">
                    {plansByYear[selectedYear]?.length || 0} plan{plansByYear[selectedYear]?.length !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <button
                onClick={() => {
                  setIsYearPlansModalOpen(false);
                  setSelectedYear(null);
                }}
                className="text-white hover:bg-white/20 rounded-full p-2 transition-colors"
              >
                <X size={24} />
              </button>
            </div>
            <div className="overflow-y-auto flex-1 p-6 bg-gray-50">
              <div className="space-y-5">
                {plansByYear[selectedYear]?.map((plan, index) => (
                  <div
                    key={plan.id}
                    className={`border-2 rounded-xl p-5 shadow-md hover:shadow-lg transition-shadow ${
                      plan.status === "accepted"
                        ? "border-green-400 bg-gradient-to-br from-green-50 to-green-100"
                        : plan.status === "rejected"
                        ? "border-red-400 bg-gradient-to-br from-red-50 to-red-100"
                        : "border-yellow-400 bg-gradient-to-br from-yellow-50 to-yellow-100"
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(plan.status)}`}>
                            {getStatusIcon(plan.status)}
                            {plan.status === "accepted" ? "Accepted" : plan.status === "rejected" ? "Rejected" : "Pending Review"}
                          </span>
                          {plan.createdAt && (
                            <span className="text-xs text-gray-600 flex items-center gap-1">
                              <Calendar size={12} />
                              {new Date(plan.createdAt).toLocaleDateString()}
                            </span>
                          )}
                        </div>
                        <h4 className="font-bold text-lg text-gray-800 mb-2">{plan.planName}</h4>
                        <div className="space-y-2 text-sm text-gray-700">
                          <div>
                            <span className="font-semibold">Activities:</span>
                            <p className="mt-1">{plan.activities}</p>
                          </div>
                          <div>
                            <span className="font-semibold">Target Date:</span>
                            <p className="mt-1">{plan.targetDate ? new Date(plan.targetDate).toLocaleDateString() : "N/A"}</p>
                          </div>
                          <div>
                            <span className="font-semibold">Expected Outcome:</span>
                            <p className="mt-1">{plan.expectedOutcome}</p>
                          </div>
                        </div>
                        {plan.adminNotes && (
                          <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                            <p className="text-xs font-semibold text-gray-700 mb-1">Admin Notes:</p>
                            <p className="text-sm text-gray-600">{plan.adminNotes}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 ml-4">
                        {plan.status !== "accepted" && (
                          <>
                            <button
                              onClick={() => {
                                setIsYearPlansModalOpen(false);
                                handleEditPlan(plan);
                              }}
                              className="text-[#241763] hover:text-[#1a1247] p-2 hover:bg-gray-100 rounded-lg transition-colors"
                              title="Edit Plan"
                            >
                              <Edit2 size={18} />
                            </button>
                            <button
                              onClick={() => handleDeletePlan(plan.id)}
                              className="text-red-500 hover:text-red-700 p-2 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Plan"
                            >
                              <Trash2 size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyAPDP;

